import React, { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { DialogBackdrop } from '@/components/ui/dialog-backdrop';
import {
  type GeneratePartnerEmailResponse,
  type ImageStyle,
  useGeneratePartnerEmail,
} from './useGeneratePartnerEmail';
import styles from './AIEmailGeneratorDialog.module.css';

const IMAGE_STYLE_STORAGE_KEY = 'reach-ai-image-style';

const IMAGE_STYLE_OPTIONS: { value: ImageStyle; label: string }[] = [
  { value: 'illustration', label: 'Illustration' },
  { value: 'photorealistic', label: 'Photo-like' },
  { value: 'graphic_design', label: 'Bold & Colorful' },
  { value: 'cartoon', label: 'Cartoon' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'watercolor', label: 'Watercolor' },
];

interface AIEmailGeneratorDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerated: (data: GeneratePartnerEmailResponse) => void;
}

export const AIEmailGeneratorDialog: React.FC<AIEmailGeneratorDialogProps> = ({
  open,
  onClose,
  onGenerated,
}) => {
  const [prompt, setPrompt] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [imageStyle, setImageStyle] = useState<ImageStyle>(() => {
    if (typeof window !== 'undefined') {
      const savedStyle = localStorage.getItem(IMAGE_STYLE_STORAGE_KEY);
      if (savedStyle && IMAGE_STYLE_OPTIONS.some(option => option.value === savedStyle)) {
        return savedStyle as ImageStyle;
      }
    }
    return 'illustration';
  });

  const { mutateAsync, isPending } = useGeneratePartnerEmail();

  if (!open) return null;

  const handleStyleChange = (nextStyle: string) => {
    const typedStyle = nextStyle as ImageStyle;
    setImageStyle(typedStyle);
    localStorage.setItem(IMAGE_STYLE_STORAGE_KEY, typedStyle);
  };

  const handleGenerate = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) return;

    setErrorMessage('');
    try {
      const generatedContent = await mutateAsync({
        userPrompt: trimmedPrompt,
        imageStyle,
      });
      onGenerated(generatedContent);
      onClose();
    } catch {
      setErrorMessage("We couldn't generate an image. Please try again.");
    }
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header className={styles.header}>
          <div>
            <div className={styles.titleWrap}>
              <h3 className={styles.title}>
                <Sparkles size={16} />
                Generate Content with AI
              </h3>
              <span className={styles.badge}>BETA</span>
            </div>
            <p className={styles.subtitle}>
              Describe the message you want and we'll generate email + SMS content.
            </p>
          </div>
          <button className={styles.close} onClick={onClose} disabled={isPending} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className={styles.form}>
          <label className={styles.fieldLabel} htmlFor="ai-prompt">
            Prompt
          </label>
          <textarea
            id="ai-prompt"
            className={styles.textarea}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="e.g., Send a promotional email announcing a 20% off sale on all products"
            disabled={isPending}
          />

          <label className={styles.fieldLabel} htmlFor="ai-image-style">
            Image Style
          </label>
          <select
            id="ai-image-style"
            className={styles.select}
            value={imageStyle}
            onChange={e => handleStyleChange(e.target.value)}
            disabled={isPending}>
            {IMAGE_STYLE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {errorMessage && <p className={styles.error}>{errorMessage}</p>}
        </div>

        <footer className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose} disabled={isPending}>
            Cancel
          </button>
          <button
            className={styles.generateButton}
            onClick={handleGenerate}
            disabled={isPending || !prompt.trim()}>
            {isPending && <span className={styles.spinner} aria-hidden />}
            Generate
          </button>
        </footer>
      </dialog>
    </>
  );
};
